import { alpha, Avatar, Box, useTheme } from "@mui/material";
import CreditCardOutlined from "@mui/icons-material/CreditCardOutlined";
import ArrowForward from "@mui/icons-material/ArrowForward";
import LockOutlined from "@mui/icons-material/LockOutlined";
import TaskAlt from "@mui/icons-material/TaskAlt";
import HighlightOff from "@mui/icons-material/HighlightOff";
import { glowPulse, pop, pulseRing, spin, twinkle } from "utils/animations";

export const LoadingHeaderIcon = ({
  color = "primary",
  icon = "card",
  size = 52,
}: {
  color?: "primary" | "secondary" | "error" | "warning" | "info" | "success";
  icon?: "card" | "arrow" | "lock";
  size?: number;
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{ position: "relative", width: size, height: size, flexShrink: 0 }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          border: `3px solid ${theme.palette.divider}`,
          borderTopColor: theme.palette[color].main,
          animation: `${spin} 1s linear infinite`,
        }}
      />
      <Avatar
        sx={{
          position: "absolute",
          inset: size / 7, //7
          bgcolor: alpha(theme.palette[color].main, 0.1),
          color: theme.palette[color].main,
          width: "auto",
          height: "auto",
        }}
      >
        {icon === "card" ? (
          <CreditCardOutlined fontSize="small" />
        ) : icon === "arrow" ? (
          <ArrowForward fontSize="small" />
        ) : (
          <LockOutlined fontSize="small" />
        )}
      </Avatar>
    </Box>
  );
};

export const FailedHeaderIcon = ({
  color = "error",
  size = 52,
}: {
  color?: "primary" | "secondary" | "error" | "warning" | "info" | "success";
  size?: number;
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        color: theme.palette[color].main,
        display: "grid",
        placeItems: "center",
        animation: `${pop} .55s cubic-bezier(.2,1.4,.4,1)`,
      }}
    >
      <HighlightOff sx={{ fontSize: size }} />
    </Box>
  );
};

export const SuccessHeaderIcon = ({
  color = "success",
  size = 52,
}: {
  color?: "primary" | "secondary" | "error" | "warning" | "info" | "success";
  size?: number;
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        width: size,
        height: size,
        position: "relative",
        color: theme.palette[color].main,
        flexShrink: 0,
      }}
    >
      {/* Pulse ring overlay */}
      <Box
        component="span"
        sx={{
          position: "absolute",
          inset: -2,
          borderRadius: "50%",
          border: `2px solid currentColor`,
          opacity: 0.6,
          animation: `${pulseRing} 1.4s ease-out 1`,
        }}
      />
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "grid",
          placeItems: "center",
          animation: `${pop} .55s cubic-bezier(.2,1.4,.4,1)`,
        }}
      >
        <TaskAlt sx={{ fontSize: size }} />
      </Box>
    </Box>
  );
};

export const CelebrationIcon = ({
  color = "primary",
  size = 75,
}: {
  color?: "primary" | "secondary" | "error" | "warning" | "info" | "success";
  size?: number;
}) => {
  const theme = useTheme();
  const accent = theme.palette[color].light;
  const accentDark = theme.palette[color].main;
  const gradId = `grad-${color}`;

  return (
    <Box
      sx={{
        position: "relative",
        flexShrink: 0,
        width: size,
        height: size,
      }}
    >
      {/* Halo */}
      <Box
        sx={{
          position: "absolute",
          inset: "12%",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${accent}44 0%, transparent 70%)`,
          animation: `${glowPulse} 3s ease-in-out infinite`,
        }}
      />

      {/* Main 8-point sparkle */}
      <Box
        component="svg"
        viewBox="0 0 100 100"
        sx={{
          position: "absolute",
          inset: 0,
          animation: `${pop} .55s cubic-bezier(.2,1.4,.4,1)`,
        }}
      >
        <defs>
          <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={accent} />
            <stop offset="100%" stopColor={accentDark} />
          </linearGradient>
        </defs>
        <path
          d="M50 14 L56 38 L72 32 L60 46 L86 50 L60 54 L72 68 L56 62 L50 86 L44 62 L28 68 L40 54 L14 50 L40 46 L28 32 L44 38 Z"
          fill={`url(#${gradId})`}
        />
        <circle cx="50" cy="50" r="8" fill="#fff" opacity="0.35" />
      </Box>

      {/* Twinkling smaller sparkles */}
      <Box
        component="svg"
        viewBox="0 0 100 100"
        sx={{ position: "absolute", inset: 0, overflow: "visible" }}
      >
        <MiniSparkle cx={12} cy={22} size={5} delay={0.2} color={accent} />
        <MiniSparkle cx={88} cy={20} size={4} delay={0.9} color={accent} />
        <MiniSparkle cx={92} cy={70} size={5} delay={1.6} color={accent} />
        <MiniSparkle cx={8} cy={75} size={4} delay={2.3} color={accent} />
        <MiniSparkle cx={50} cy={6} size={3} delay={3.0} color={accent} />
      </Box>
    </Box>
  );
};

const MiniSparkle = ({
  cx,
  cy,
  size,
  delay,
  color,
}: {
  cx: number;
  cy: number;
  size: number;
  delay: number;
  color: string;
}) => {
  const s = size;
  const t = size * 0.28;
  const d = `
    M${cx} ${cy - s}
    Q${cx + t} ${cy - t}, ${cx + s} ${cy}
    Q${cx + t} ${cy + t}, ${cx} ${cy + s}
    Q${cx - t} ${cy + t}, ${cx - s} ${cy}
    Q${cx - t} ${cy - t}, ${cx} ${cy - s} Z
  `;
  return (
    <Box
      component="path"
      d={d}
      fill={color}
      sx={{
        transformOrigin: `${cx}px ${cy}px`,
        animation: `${twinkle} 3.6s ease-in-out ${delay}s infinite`,
      }}
    />
  );
};
